import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { Link } from 'react-router-dom';
import api, { API_ENDPOINTS } from '../config/api';
import {
  Search,
  Filter,
  MessageCircle,
  Clock,
  User,
  Briefcase,
  Grid,
  MoreVertical,
  Star,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const ChatListPage = () => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    page: 1
  });

  // Fetch user's chats
  const { data: chatsData, isLoading, error, refetch } = useQuery(
    ['chats', filters],
    () => api.get(API_ENDPOINTS.CHATS.LIST, { params: filters }),
    {
      select: (data) => data.data,
      keepPreviousData: true,
    }
  );

  // Listen for new messages to update chat list
  useEffect(() => {
    if (socket) {
      const handleNewMessage = (message) => {
        console.log('ChatList: New message received, refetching chat list');
        // Refetch chats to update last message and unread counts
        refetch();
      };

      socket.on('new-message', handleNewMessage);

      return () => {
        socket.off('new-message', handleNewMessage);
      };
    }
  }, [socket, refetch]);

  const chats = chatsData?.chats || [];
  const totalPages = chatsData?.totalPages || 1;
  const currentPage = chatsData?.currentPage || 1;

  const chatTypes = [
    { value: '', label: 'All Types' },
    { value: 'job', label: 'Job Chats' },
    { value: 'gig', label: 'Gig Chats' },
    { value: 'general', label: 'General' }
  ];

  const statuses = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' },
    { value: 'completed', label: 'Completed' }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getUnreadCount = (chat) => {
    try {
      if (!chat?.unreadCount || !user?._id) return 0;
      
      // Handle both Map and object formats
      if (chat.unreadCount instanceof Map) {
        return chat.unreadCount.get(user._id.toString()) || 0;
      } else if (typeof chat.unreadCount === 'object') {
        return chat.unreadCount[user._id.toString()] || 0;
      }
      
      return 0;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  };

  const getOtherParticipant = (chat) => {
    if (!chat.participants || !user?._id) return null;
    return chat.participants.find(p => {
      const participantId = p.user._id || p.user;
      return participantId.toString() !== user._id.toString();
    });
  };

  const getChatTypeIcon = (type) => {
    switch (type) {
      case 'job':
        return <Briefcase className="w-4 h-4" />;
      case 'gig':
        return <Grid className="w-4 h-4" />;
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const getChatTypeColor = (type) => {
    switch (type) {
      case 'job':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'gig':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'archived':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'completed':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatLastMessage = (chat) => {
    if (!chat.lastMessage) return 'No messages yet';
    
    const { content, sender, timestamp } = chat.lastMessage;
    const isOwnMessage = sender?._id === user?._id || sender?.toString() === user?._id;
    const prefix = isOwnMessage ? 'You: ' : '';
    
    return `${prefix}${content}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading conversations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Error loading chats
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error.message || 'Something went wrong while loading your conversations.'}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Messages
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Manage your conversations and collaborate with clients and talents
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent">
                  <Filter className="w-4 h-4 mr-2" />
                  Filter
                </button>
                <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent">
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                />
              </div>
            </div>

            {/* Chat Type */}
            <div>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                {chatTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
              >
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Chat List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {chats.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💬</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No conversations yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Start collaborating by applying to jobs or ordering gigs.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {chats.map((chat) => {
              const otherParticipant = getOtherParticipant(chat);
              const unreadCount = getUnreadCount(chat);
              
              return (
                <Link
                  key={chat._id}
                  to={`/chats/${chat._id}`}
                  className="block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="p-6">
                    <div className="flex items-center space-x-4">
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center text-white font-medium">
                          {otherParticipant?.user?.profile?.avatar ? (
                            <img
                              src={otherParticipant.user.profile.avatar}
                              alt={otherParticipant.user.username || otherParticipant.user.email}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-lg">
                              {(otherParticipant?.user?.username || otherParticipant?.user?.email || 'U')[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Chat Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {otherParticipant?.user?.username || otherParticipant?.user?.email || 'Unknown User'}
                            </h3>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getChatTypeColor(chat.type)}`}>
                              {getChatTypeIcon(chat.type)}
                              <span className="ml-1 capitalize">{chat.type}</span>
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(chat.status)}`}>
                              {chat.status}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {formatTimestamp(chat.lastMessage?.timestamp)}
                            </span>
                            {unreadCount > 0 && (
                              <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                            <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 truncate">
                          {formatLastMessage(chat)}
                        </p>
                        
                        {chat.job && (
                          <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                            Job: {chat.job.title}
                          </p>
                        )}
                        {chat.gig && (
                          <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                            Gig: {chat.gig.title}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;

              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm rounded-lg ${
                    page === currentPage
                      ? 'bg-accent text-white'
                      : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatListPage;